'use client';

import { Grid, TextField } from '@petsmart-ui/sparky';
import { useFormContext } from 'react-hook-form';

import { useWindowWidth } from '@libs/common-next';

import type { NameFieldsProps } from './types';

// Formik implementation
function RenderNameFieldsFormik({
    values,
    touched,
    errors,
    onBlur,
    onChange,
}: Readonly<Extract<NameFieldsProps, { formLibrary?: 'formik' }>>) {
    return (
        <>
            <Grid.Item>
                <TextField
                    data-testid="firstName"
                    data-cs-mask
                    wrapperTestId="firstName-field"
                    id="firstName"
                    label="First name"
                    name="firstName"
                    type="text"
                    value={values.firstName}
                    onBlur={onBlur}
                    onChange={onChange}
                    isError={touched.firstName && !!errors.firstName}
                    fieldNote={touched.firstName && errors.firstName?.[0]}
                />
            </Grid.Item>
            <Grid.Item>
                <TextField
                    data-testid="lastName"
                    data-cs-mask
                    wrapperTestId="lastName-field"
                    id="lastName"
                    label="Last name"
                    name="lastName"
                    type="text"
                    value={values.lastName}
                    onBlur={onBlur}
                    onChange={onChange}
                    isError={touched.lastName && !!errors.lastName}
                    fieldNote={touched.lastName && errors.lastName?.[0]}
                />
            </Grid.Item>
        </>
    );
}

// RHF implementation
function RenderNameFieldsRHF() {
    const { register, formState } = useFormContext<{ firstName: string; lastName: string }>();
    const { errors, touchedFields, isSubmitted } = formState;

    const showFirstNameError = (touchedFields.firstName || isSubmitted) && errors.firstName;
    const showLastNameError = (touchedFields.lastName || isSubmitted) && errors.lastName;

    return (
        <>
            <Grid.Item>
                <TextField
                    data-testid="firstName"
                    data-cs-mask
                    wrapperTestId="firstName-field"
                    id="firstName"
                    label="First name"
                    type="text"
                    {...register('firstName')}
                    isError={!!showFirstNameError}
                    fieldNote={showFirstNameError ? errors.firstName?.message : undefined}
                />
            </Grid.Item>
            <Grid.Item>
                <TextField
                    data-testid="lastName"
                    data-cs-mask
                    wrapperTestId="lastName-field"
                    id="lastName"
                    label="Last name"
                    type="text"
                    {...register('lastName')}
                    isError={!!showLastNameError}
                    fieldNote={showLastNameError ? errors.lastName?.message : undefined}
                />
            </Grid.Item>
        </>
    );
}

export function NameFields({ vertical = false, ...props }: NameFieldsProps) {
    const { isMobile } = useWindowWidth();
    const isRHF = props.formLibrary === 'rhf';

    const content = isRHF ? (
        <RenderNameFieldsRHF />
    ) : (
        <RenderNameFieldsFormik {...(props as Extract<NameFieldsProps, { formLibrary?: 'formik' }>)} />
    );

    return !vertical && !isMobile ? (
        <Grid.Item>
            <Grid variant="side-by-side" gap="sm">
                {content}
            </Grid>
        </Grid.Item>
    ) : (
        content
    );
}
